import Card from "../UI/Card";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  TimeScale,
} from "chart.js";
import Tooltip from "@mui/material/Tooltip";
import zoomPlugin from "chartjs-plugin-zoom";
import { useMessageFeed } from "../../MessageFeedContext";
import "chartjs-adapter-moment";
import { ChartScaleNotice } from "../History/ChartScaleNotice";
import {
  canUseLogarithmicScale,
  hasValuesOutsideRange,
  type ChartScaleMode,
} from "../History/chartScale";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  TimeScale,
  zoomPlugin
);

export const baseOptions = {
  maintainAspectRatio: false,
  responsive: true,
  animation: true,
  normalized: true,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  stacked: false,
  plugins: {
    title: {
      display: true,
      text: "",
    },
    // Limita la cantidad de puntos que se dibujan para no degradar la performance.
    // Mantiene el dataset completo para permitir pan/zoom y carga de históricos.
    decimation: {
      enabled: true,
      algorithm: "lttb",
      // Configurable vía REACT_APP_MONITOR_MAX_POINTS (por defecto 1500)
      // @ts-ignore - value provided at runtime
      samples: parseInt(process.env.REACT_APP_MONITOR_MAX_POINTS || "5000", 10),
    },
    tooltip: {
      callbacks: {
        label: function (tooltipItem: any) {
          let value = tooltipItem.parsed.y; // Asegurar que obtenemos el valor correcto
          let datasetLabel = tooltipItem.dataset.label || "";

          if (datasetLabel.toLowerCase().includes("conductividad")) {
            return `${datasetLabel}: ${value} µS/cm`;
          } else if (datasetLabel.toLowerCase().includes("voltaje")) {
            return `${datasetLabel}: ${value} V`;
          } else if (datasetLabel.toLowerCase().includes("temperatura")) {
            return `${datasetLabel}: ${value} °C`;
          }

          return `${datasetLabel}: ${value}`;
        },
      },
    },
  },
  scales: {
    x: {
      type: "time" as const,
      gridLines: {
        lineWidth: 2,
      },
      time: {
        unit: "minute" as const,
        displayFormats: {
          minute: "HH:mm:ss",
          hour: "HH:mm",
          day: "MMM DD",
        },
      },
    },
    y: {
      type: "linear" as const,
      display: true,
      position: "left" as const,
      // Los rangos se pueden sobrescribir dinámicamente
    },
  },
};

// Función helper para aplicar rangos y escala del eje Y
const applyYAxisRange = (
  options: any,
  yAxisRange: { min: number; max: number } | undefined,
  scaleMode: ChartScaleMode
) => ({
  ...options,
  scales: {
    ...options.scales,
    y: {
      ...options.scales.y,
      type: scaleMode === "logarithmic" ? "logarithmic" : "linear",
      ...(scaleMode === "fixed" && yAxisRange ? {
        min: yAxisRange.min,
        max: yAxisRange.max,
      } : {}),
    },
  },
});

interface Message {
  timestamp?: string;
  [key: string]: any; // Para permitir cualquier métrica (temperatura, conductividad, etc.)
}

const RealTimeGraph = (props: {
  title: string;
  label: string;
  borderColor: string;
  backgroundColor: string;
  messageFeed: Message[]; // Recibimos el feed completo
  metricKey: string; // Clave de la métrica a graficar (ej: "temperatura", "conductividad")
  headerColor?: string;
  textColor?: string;
  contextId: string;
  yAxisRange?: { min: number; max: number }; // Rango fijo del eje Y
}) => {
  const [data, setData] = useState({
    labels: [] as Date[],
    datasets: [] as any[],
  });

  const { loadOlderMessages, trimToRange } = useMessageFeed();

  const chartRef = useRef<any>(null);
  const [scaleMode, setScaleMode] = useState<ChartScaleMode>("fixed");
  const lastXMinRef = useRef<number | undefined>(undefined);
  const oldestTsRef = useRef<string | undefined>();
  const loadingOlderRef = useRef<boolean>(false);

  const scaleAssessment = useMemo(() => {
    const values = props.messageFeed
      .map((message) => Number(message[props.metricKey]))
      .filter(Number.isFinite);

    return {
      clipped: hasValuesOutsideRange(values, props.yAxisRange),
      canLogarithmicScale: canUseLogarithmicScale(values),
    };
  }, [props.messageFeed, props.metricKey, props.yAxisRange]);

  useEffect(() => {
    if (scaleMode === "logarithmic" && !scaleAssessment.canLogarithmicScale) {
      setScaleMode("fixed");
    }
  }, [scaleAssessment.canLogarithmicScale, scaleMode]);

  useEffect(() => {
    setScaleMode("fixed");
  }, [props.metricKey]);
  const loadOlderData = useCallback(async () => {
    if (!props.messageFeed.length) return;
    const oldest = props.messageFeed[0];
    if (!oldest.timestamp) return;
    if (loadingOlderRef.current) return;
    try {
      loadingOlderRef.current = true;
      await loadOlderMessages(props.contextId, oldest.timestamp);
    } finally {
      loadingOlderRef.current = false;
    }
  }, [props.messageFeed, props.contextId, loadOlderMessages]);

  const oldestTimestamp = props.messageFeed[0]?.timestamp;

  const chartOptions = useMemo(() => {
    const min = oldestTimestamp ? new Date(oldestTimestamp).getTime() : undefined;
    const options = {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        zoom: {
          pan: {
            enabled: true,
            mode: "x" as const,
            // Cargar datos mientras se desplaza para ver el scroll continuo
            onPan: ({ chart }: any) => {
              const xMin: number = chart.scales.x.min as number;
              // Paneo hacia la izquierda: si tocamos el borde, cargar más datos
              if (min !== undefined && xMin <= min) {
                loadOlderData();
              }
            },
            // Al completar paneo, si fue hacia la derecha, recortar al rango visible
            onPanComplete: ({ chart }: any) => {
              const xScale = chart.scales.x;
              const curMin: number = xScale.min as number;
              const curMax: number = xScale.max as number;
              const prevMin = lastXMinRef.current;
              // Si el min actual es mayor que el previo, se paneó a la derecha
              if (prevMin !== undefined && curMin > prevMin) {
                try {
                  const startISO = new Date(curMin).toISOString();
                  const endISO = new Date(curMax).toISOString();
                  trimToRange(props.contextId, startISO, endISO);
                } catch (_) { }
              }
              lastXMinRef.current = curMin;
            },
          },
          // Quitamos el límite mínimo para permitir el desplazamiento visual hacia la izquierda
        },
      },
    } as any;

    return applyYAxisRange(options, props.yAxisRange, scaleMode);
  }, [loadOlderData, oldestTimestamp, props.contextId, trimToRange, props.yAxisRange, scaleMode]);

  useEffect(() => {
    // Filtrar mensajes que tengan timestamp y el valor de la métrica
    const validMessages = props.messageFeed.filter(
      (m) => m.timestamp && m[props.metricKey] !== undefined
    );

    // Mantener una ventana acotada de puntos para no degradar el navegador
    const maxPoints = parseInt(
      process.env.REACT_APP_MONITOR_MAX_POINTS || "2000",
      10
    );
    const recent = validMessages.slice(-maxPoints);
    // Asegurar orden cronológico ascendente para evitar segmentos "de regreso".
    const ordered = [...recent].sort(
      (a, b) => new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime()
    );

    const labels = ordered.map((m) => new Date(m.timestamp!));
    const values = ordered.map((m) => m[props.metricKey]);

    setData({
      labels,
      datasets: [
        {
          label: props.label,
          data: labels.map((timestamp, index) => ({
            x: timestamp,
            y: values[index],
          })),
          parsing: false,
          borderColor: props.borderColor,
          backgroundColor: props.backgroundColor,
          yAxisID: "y",
          pointRadius: 0,
          // Evitar que Chart.js dibuje el segmento si por algún motivo el siguiente punto
          // tiene un timestamp menor (artefacto típico al cargar datos en movimiento).
          segment: {
            borderColor: (ctx: any) =>
              ctx.p0?.parsed?.x > ctx.p1?.parsed?.x ? "rgba(0,0,0,0)" : undefined,
          },
        },
      ],
    });
  }, [
    props.backgroundColor,
    props.borderColor,
    props.label,
    props.messageFeed,
    props.metricKey,
  ]);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart && oldestTimestamp && oldestTimestamp !== oldestTsRef.current) {
      oldestTsRef.current = oldestTimestamp;
      chart.resetZoom();
    }
  }, [oldestTimestamp]);

  return (
    <section>
      <Card title={props.title} headerColor={props.headerColor} textColor={props.textColor}>
        <ChartScaleNotice
          clipped={scaleAssessment.clipped}
          canLogarithmicScale={scaleAssessment.canLogarithmicScale}
          scaleMode={scaleMode}
          onScaleModeChange={setScaleMode}
        />
        <Tooltip
          title="Desplazá: hacia la izquierda amplía la ventana de monitoreo; hacia la derecha la reduce"
          placement="top"
          arrow
        >
          <div>
            {data.labels.length === 0 && "No existen datos para mostrar."}
            {data.labels.length > 0 && (
              <Line ref={chartRef} options={chartOptions} data={data} />
            )}
          </div>
        </Tooltip>
      </Card>
    </section>
  );
};

export default RealTimeGraph;
